import { Component, OnInit, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { CommonModule } from '@angular/common';
import { Subject, EMPTY, forkJoin } from 'rxjs';
import { takeUntil, switchMap } from 'rxjs/operators';
import { DragDropModule, CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { AdminService } from '../../services/admin.service';
import type { SurveyVersionDto, SurveyQuestionDto, SurveyAnswerDto } from '../../models/admin-dto';

@Component({
  selector: 'app-survey-preview',
  standalone: true,
  imports: [CommonModule, DragDropModule, RouterLink],
  templateUrl: './survey-preview.component.html',
  styleUrl: './survey-preview.component.css'
})
export class SurveyPreviewComponent implements OnInit, OnDestroy {
  typeId = 0;
  versionId = 0;
  version: SurveyVersionDto | null = null;
  errorMessage: string | null = null;
  editQuestions: SurveyQuestionDto[] = [];
  editAnswersByQuestion = new Map<number, SurveyAnswerDto[]>();
  editReady = false;
  /** Which field is being edited (inline click-to-edit). */
  editingQuestionId: number | null = null;
  editingAnswerId: number | null = null;
  editingScoreId: number | null = null;
  publishInProgress = false;
  private readonly destroy$ = new Subject<void>();

  get isDraft(): boolean {
    return (this.version?.surveyStateCode ?? '') === 'Draft';
  }

  constructor(
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly admin: AdminService,
    private readonly cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.route.queryParams
      .pipe(
        takeUntil(this.destroy$),
        switchMap((qParams) => {
          this.typeId = parseInt(this.route.snapshot.paramMap.get('id') ?? '0', 10);
          const vid = parseInt(qParams['versionId'] ?? '0', 10);
          if (!vid) {
            this.errorMessage = 'Missing versionId in URL.';
            return EMPTY;
          }
          this.versionId = vid;
          return this.admin.getSurveyVersion(vid);
        })
      )
      .subscribe({
        next: (version) => {
          if (!version) return;
          this.version = version;
          this.admin.markSurveyVersionPreviewed(version.id).subscribe({ error: () => {} });
          this.buildPreview(version);
        }
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  getEditAnswers(questionId: number): SurveyAnswerDto[] {
    return this.editAnswersByQuestion.get(questionId) ?? [];
  }

  startEditQuestion(q: SurveyQuestionDto): void {
    this.editingAnswerId = null;
    this.editingScoreId = null;
    this.editingQuestionId = q.id;
    this.cdr.markForCheck();
  }

  startEditAnswerText(a: SurveyAnswerDto): void {
    this.editingQuestionId = null;
    this.editingScoreId = null;
    this.editingAnswerId = a.id;
    this.cdr.markForCheck();
  }

  startEditScore(a: SurveyAnswerDto): void {
    this.editingQuestionId = null;
    this.editingAnswerId = null;
    this.editingScoreId = a.id;
    this.cdr.markForCheck();
  }

  private invalidatePreview(): void {
    if (!this.versionId) return;
    this.admin
      .updateSurveyVersion(this.versionId, { hasBeenPreviewed: false })
      .pipe(takeUntil(this.destroy$))
      .subscribe({ error: () => {} });
  }

  saveQuestionText(q: SurveyQuestionDto, event: Event): void {
    const value = (event.target as HTMLInputElement).value?.trim() ?? '';
    this.editingQuestionId = null;
    if (q.questionText !== value) {
      q.questionText = value;
      this.invalidatePreview();
      this.admin.updateSurveyQuestion(q.id, { questionText: value }).pipe(takeUntil(this.destroy$)).subscribe({
        error: (err: Error) => {
          this.errorMessage = err?.message ?? 'Failed to save.';
          this.cdr.markForCheck();
        }
      });
    }
    this.cdr.markForCheck();
  }

  saveAnswerText(a: SurveyAnswerDto, event: Event): void {
    const value = (event.target as HTMLInputElement).value?.trim() ?? '';
    this.editingAnswerId = null;
    if (a.answerText !== value) {
      a.answerText = value;
      this.invalidatePreview();
      this.admin.updateSurveyAnswer(a.id, { answerText: value }).pipe(takeUntil(this.destroy$)).subscribe({
        error: (err: Error) => {
          this.errorMessage = err?.message ?? 'Failed to save.';
          this.cdr.markForCheck();
        }
      });
    }
    this.cdr.markForCheck();
  }

  saveAnswerScore(a: SurveyAnswerDto, event: Event): void {
    const raw = (event.target as HTMLInputElement).value;
    const value = raw !== '' ? parseInt(raw, 10) : 0;
    this.editingScoreId = null;
    const num = Number.isFinite(value) ? value : 0;
    if (a.score !== num) {
      a.score = num;
      this.invalidatePreview();
      this.admin.updateSurveyAnswer(a.id, { score: num }).pipe(takeUntil(this.destroy$)).subscribe({
        error: (err: Error) => {
          this.errorMessage = err?.message ?? 'Failed to save.';
          this.cdr.markForCheck();
        }
      });
    }
    this.cdr.markForCheck();
  }

  publishVersion(): void {
    if (!this.versionId || !this.isDraft) return;
    if (!confirm('Publish this survey version? It will be available for respondents.')) return;
    this.publishInProgress = true;
    this.errorMessage = null;
    this.cdr.markForCheck();
    this.admin
      .setSurveyVersionState(this.versionId, { stateCode: 'Published' })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.publishInProgress = false;
          this.cdr.markForCheck();
          this.router.navigate(['/admin/survey-types', this.typeId, 'edit'], { queryParams: this.versionId ? { versionId: this.versionId } : {} });
        },
        error: (err: Error) => {
          this.errorMessage = err?.message ?? 'Failed to publish.';
          this.publishInProgress = false;
          this.cdr.markForCheck();
        }
      });
  }

  onQuestionDrop(event: CdkDragDrop<SurveyQuestionDto[]>): void {
    if (event.previousIndex === event.currentIndex) return;
    moveItemInArray(this.editQuestions, event.previousIndex, event.currentIndex);
    this.invalidatePreview();
    this.editQuestions.forEach((q, i) => {
      q.displayOrder = i;
      this.admin.updateSurveyQuestion(q.id, { displayOrder: i }).pipe(takeUntil(this.destroy$)).subscribe({
        error: (err: Error) => {
          this.errorMessage = err?.message ?? 'Failed to reorder.';
          this.cdr.markForCheck();
        }
      });
    });
    this.cdr.markForCheck();
  }

  private buildPreview(version: SurveyVersionDto): void {
    this.admin
      .getSurveyQuestionsByVersion(version.id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (questions) => {
          const qSorted = (questions ?? []).sort((a, b) => a.displayOrder - b.displayOrder);
          if (qSorted.length === 0) {
            this.editQuestions = [];
            this.editAnswersByQuestion = new Map();
            this.editReady = true;
            this.cdr.markForCheck();
            return;
          }
          forkJoin(qSorted.map((q) => this.admin.getSurveyAnswersByQuestion(q.id)))
            .pipe(takeUntil(this.destroy$))
            .subscribe({
              next: (answersArrays) => {
                this.editQuestions = qSorted;
                this.editAnswersByQuestion = new Map(
                  qSorted.map((q, i) => [q.id, (answersArrays[i] ?? []).sort((a, b) => a.displayOrder - b.displayOrder)])
                );
                this.editReady = true;
                this.cdr.markForCheck();
              },
              error: (err: Error) => {
                this.errorMessage = err?.message ?? 'Failed to load preview.';
                this.cdr.markForCheck();
              }
            });
        },
        error: (err: Error) => {
          this.errorMessage = err?.message ?? 'Failed to load preview.';
          this.cdr.markForCheck();
        }
      });
  }
}
